<template>
  <div class="link-types-management row">
    <div class="col-12 col-lg-8">
      <b-card>
        <div class="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
          <h4 class="mb-0">Link Tipleri</h4>
          <b-badge pill variant="info">{{ linkTypes.length }} kayıt</b-badge>
        </div>

        <b-table
          :items="linkTypes"
          :fields="fields"
          bordered
          responsive
          hover
          small
        >
          <template #cell(key)="row">
            <code>{{ row.item.key }}</code>
          </template>

          <template #cell(short_link_domain)="row">
            <span class="text-muted small">
              {{ row.item.short_link_domain || 'Varsayılan kısa link' }}
            </span>
          </template>

          <template #cell(use_category)="row">
            <b-form-checkbox
              :checked="row.item.use_category"
              @change="toggleUseCategory(row.item)"
            >
              <b-badge pill :variant="row.item.use_category ? 'success' : 'secondary'">
                {{ row.item.use_category ? 'Evet' : 'Hayır' }}
              </b-badge>
            </b-form-checkbox>
          </template>

          <template #cell(status)="row">
            <b-form-checkbox
              :checked="row.item.status"
              @change="toggleStatus(row.item)"
            >
              <b-badge pill :variant="row.item.status ? 'success' : 'danger'">
                {{ row.item.status ? 'Aktif' : 'Pasif' }}
              </b-badge>
            </b-form-checkbox>
          </template>

          <template #cell(actions)="row">
            <div class="d-flex gap-1">
              <b-button
                size="sm"
                variant="primary"
                @click="editLinkType(row.item)"
              >
                <feather-icon icon="Edit2Icon" title="Düzenle" />
              </b-button>
            </div>
          </template>
        </b-table>
      </b-card>
    </div>

    <div class="col-12 col-lg-4">
      <b-card>
        <h4 class="mb-3">{{ isEditing ? 'Link Tipi Düzenle' : 'Yeni Link Tipi Ekle' }}</h4>

        <b-form @submit.prevent="submitForm">
          <b-form-group label="Key" label-for="link-type-key">
            <b-form-input
              id="link-type-key"
              v-model="form.key"
              placeholder="örn: story, bio, whatsapp"
              required
            />
          </b-form-group>

          <b-form-group label="Ad" label-for="link-type-name">
            <b-form-input
              id="link-type-name"
              v-model="form.name"
              placeholder="Link tipi adı"
              required
            />
          </b-form-group>

          <b-form-group>
            <b-form-checkbox v-model="form.use_custom_short_domain">
              Özel kısa link domain kullan
            </b-form-checkbox>
          </b-form-group>

          <b-form-group
            v-if="form.use_custom_short_domain"
            label="Short Link Domain"
            label-for="link-type-short-domain"
          >
            <b-form-input
              id="link-type-short-domain"
              v-model="form.short_link_domain"
              placeholder="ör: https://link.example.com"
            />
          </b-form-group>

          <b-form-group>
            <b-form-checkbox v-model="form.use_category">
              Kategori kullanıyor mu?
            </b-form-checkbox>
          </b-form-group>

          <b-form-group>
            <b-form-checkbox v-model="form.status">
              Aktif mi?
            </b-form-checkbox>
          </b-form-group>

          <div class="d-grid gap-2">
            <b-button type="submit" :variant="isEditing ? 'primary' : 'success'" block>
              {{ isEditing ? 'Güncelle' : 'Kaydet' }}
            </b-button>

            <b-button type="button" variant="outline-secondary" block @click="resetForm">
              Vazgeç
            </b-button>
          </div>
        </b-form>
      </b-card>
    </div>
  </div>
</template>

<script>
import {
  BButton,
  BCard,
  BForm,
  BFormInput,
  BFormGroup,
  BFormCheckbox,
  BTable,
  BBadge,
} from 'bootstrap-vue'
import FeatherIcon from '@core/components/feather-icon/FeatherIcon.vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  name: 'LinkTypesView',
  components: {
    BButton,
    BCard,
    BForm,
    BFormInput,
    BFormGroup,
    BFormCheckbox,
    BTable,
    BBadge,
    FeatherIcon,
  },
  data() {
    return {
      linkTypes: [],
      fields: [
        { key: 'key', label: 'Key', sortable: true },
        { key: 'name', label: 'Ad', sortable: true },
        { key: 'short_link_domain', label: 'Short Link Domain', sortable: false },
        { key: 'use_category', label: 'Kategori', sortable: false },
        { key: 'status', label: 'Durum', sortable: false },
        { key: 'actions', label: 'İşlemler', sortable: false },
      ],
      form: {
        id: null,
        key: '',
        name: '',
        short_link_domain: '',
        use_custom_short_domain: false,
        use_category: false,
        status: true,
      },
      isEditing: false,
    }
  },
  created() {
    this.fetchLinkTypes()
  },
  methods: {
    normalizeLinkType(item) {
      return {
        ...item,
        short_link_domain: item.short_link_domain || '',
        use_custom_short_domain: !!item.short_link_domain,
        use_category: Number(item.use_category) === 1,
        status: Number(item.status) === 1,
      }
    },
    async fetchLinkTypes() {
      try {
        const response = await this.$http.get('/link/type')
        const rows = Array.isArray(response?.data?.data) ? response.data.data : []
        this.linkTypes = rows.map(item => this.normalizeLinkType(item))
      } catch (error) {
        this.showToast('Link tipleri yüklenemedi', 'danger', this.getErrorMessage(error))
      }
    },
    async submitForm() {
      try {
        const payload = this.buildPayload()

        if (this.isEditing) {
          await this.$http.put('/link/type', payload)
          this.showToast('Link tipi güncellendi', 'primary', 'İşlem başarıyla tamamlandı.')
        } else {
          await this.$http.post('/link/type', payload)
          this.showToast('Link tipi eklendi', 'success', 'Yeni link tipi başarıyla kaydedildi.')
        }

        this.resetForm()
        await this.fetchLinkTypes()
      } catch (error) {
        this.showToast('İşlem başarısız', 'danger', this.getErrorMessage(error))
      }
    },
    buildPayload() {
      const {
        id,
        use_custom_short_domain,
        ...rest
      } = this.form

      const payload = {
        ...rest,
        short_link_domain: use_custom_short_domain ? (this.form.short_link_domain || null) : null,
        use_category: this.form.use_category ? 1 : 0,
        status: this.form.status ? 1 : 0,
      }

      if (this.isEditing && id !== null && id !== undefined) {
        payload.id = id
      }

      return payload
    },
    async toggleStatus(item) {
      const payload = {
        ...this.normalizeLinkType(item),
        status: Number(item.status) === 1 ? 0 : 1,
      }

      await this.updateLinkType(payload, 'Durum güncellendi')
    },
    async toggleUseCategory(item) {
      const payload = {
        ...this.normalizeLinkType(item),
        use_category: Number(item.use_category) === 1 ? 0 : 1,
      }

      await this.updateLinkType(payload, 'Kategori seçeneği güncellendi')
    },
    async updateLinkType(payload, toastTitle) {
      try {
        const { id, use_custom_short_domain, ...cleanPayload } = payload

        await this.$http.put('/link/type', {
          ...cleanPayload,
          id,
          short_link_domain: payload.use_custom_short_domain ? (payload.short_link_domain || null) : null,
          use_category: payload.use_category ? 1 : 0,
          status: payload.status ? 1 : 0,
        })

        this.showToast(toastTitle, 'primary', 'Güncelleme başarıyla kaydedildi.')
        await this.fetchLinkTypes()
      } catch (error) {
        this.showToast('Güncelleme başarısız', 'danger', this.getErrorMessage(error))
      }
    },
    editLinkType(item) {
      this.isEditing = true
      this.form = {
        ...this.normalizeLinkType(item),
        short_link_domain: item.short_link_domain || '',
        use_custom_short_domain: !!item.short_link_domain,
        use_category: Number(item.use_category) === 1,
        status: Number(item.status) === 1,
      }
    },
    resetForm() {
      this.isEditing = false
      this.form = {
        id: null,
        key: '',
        name: '',
        short_link_domain: '',
        use_custom_short_domain: false,
        use_category: false,
        status: true,
      }
    },
    getErrorMessage(error) {
      return error?.response?.data?.error || error?.response?.data?.message || 'Beklenmeyen bir hata oluştu.'
    },
    showToast(title, variant = 'success', text = '') {
      this.$toast({
        component: ToastificationContent,
        props: {
          title,
          icon: variant === 'danger' ? 'AlertTriangleIcon' : 'CheckIcon',
          variant,
          text,
        },
      })
    },
  },
}
</script>
